import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import { useProject } from '../../contexts/ProjectContext';
import { useFilter } from '../../contexts/FilterContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import { PERMISSIONS } from '../../utils/permissions';
import {
  getGuardrailsEnvironments,
  getGuardrailsGroups,
  getGuardrailsPolicies,
  getGuardrailsViolations,
  getComplianceStats,
  toggleGuardrailGroup,
  triggerEnvironmentScan,
} from '../../services/cloudopsApi';
import { FiShield as Shield, FiAlertTriangle as AlertTriangle, FiCheckCircle as CheckCircle2, FiRefreshCw as RefreshCw } from 'react-icons/fi';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { markCloudOpsStepComplete } from '../../hooks/useCloudOpsOnboardingStatus';
import ViolationCard from './components/ViolationCard';
import GuardrailCard from './components/GuardrailCard';

interface GuardrailEnvironment {
  id: string;
  name?: string;
  aws_account_id?: string;
}

interface GuardrailGroup {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  policy_count?: number;
}

interface GuardrailPolicy {
  id: string;
  name: string;
  category: string;
  is_active: boolean;
  group_ids?: string[];
}

interface GuardrailViolation {
  id: string;
  severity: string;
  [key: string]: unknown;
}

interface OverallStats {
  activeRules: number;
  openViolations: number;
  complianceScore: number | null;
}

interface ViolationsPage {
  data: GuardrailViolation[];
  meta: { total: number; page: number; limit: number } | null;
}

interface ComplianceStats {
  total_resources: number;
  compliance_score: number;
  [key: string]: unknown;
}

// Severity priority for sorting (lower number = higher priority)
const SEVERITY_PRIORITY: Record<string, number> = {
  critical: 1,
  high: 2,
  medium: 3,
  low: 4,
};

const sortViolationsBySeverity = (violations: GuardrailViolation[]): GuardrailViolation[] => {
  return [...violations].sort((a, b) => {
    const priorityA = SEVERITY_PRIORITY[a.severity] ?? 5;
    const priorityB = SEVERITY_PRIORITY[b.severity] ?? 5;
    return priorityA - priorityB;
  });
};

const GuardrailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedClientId, isLoadingClients } = useClient();
  const { selectedProjectId } = useProject();
  const { hasPermission } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const { filterType, selectedId: filterId } = useFilter();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Data state
  const [environments, setEnvironments] = useState<GuardrailEnvironment[]>([]);
  const [groups, setGroups] = useState<GuardrailGroup[]>([]);
  const [policies, setPolicies] = useState<GuardrailPolicy[]>([]);
  const [violations, setViolations] = useState<GuardrailViolation[]>([]);
  const [togglingGroupId, setTogglingGroupId] = useState<string | null>(null);
  const [scanning, setScanning] = useState<boolean>(false);

  // UI state
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Derive selected environment from global filter
  const selectedEnvironment = filterType === 'environment' && filterId ? filterId : 'all';

  // Calculate overall stats from all environments or selected environment
  const [overallStats, setOverallStats] = useState<OverallStats>({
    activeRules: 0,
    openViolations: 0,
    complianceScore: null,
  });

  // Mark onboarding step as complete when visiting this page
  useEffect(() => {
    markCloudOpsStepComplete('configure_guardrails');
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Avoid calling /guardrails/environments without client_id for partner/aws users.
      const needsClientId = isAWSUser() ||
        hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
        hasPermission(PERMISSIONS.CLIENTS_MANAGE) ||
        hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
        hasPermission(PERMISSIONS.PARTNERS_MANAGE);
      if (needsClientId && !selectedClientId) {
        if (isLoadingClients) return;
        setError('Please select a client to view guardrails data.');
        return;
      }
      const clientId = selectedClientId || user?.client_id || undefined;

      // Load environments, groups, and policies
      const [environmentsData, groupsData, policiesResponse] = await Promise.all([
        getGuardrailsEnvironments(clientId),
        getGuardrailsGroups(clientId),
        getGuardrailsPolicies(undefined, undefined, 1, 50),
      ]);

      setEnvironments(environmentsData || []);
      setGroups(groupsData || []);
      const policiesList = policiesResponse?.data ?? (Array.isArray(policiesResponse) ? policiesResponse : []);
      setPolicies(policiesList);

      // Validate selected environment exists in loaded environments
      let validSelectedEnvironment = selectedEnvironment;
      if (selectedEnvironment !== 'all' && (environmentsData?.length ?? 0) > 0) {
        const envExists = environmentsData.some((env: GuardrailEnvironment) => env.id === selectedEnvironment);
        if (!envExists) {
          // Environment from global filter doesn't exist in this client's environments, treat as 'all'
          validSelectedEnvironment = 'all';
        }
      }

      // Load violations and compliance stats based on selected environment
      if ((environmentsData?.length ?? 0) > 0) {
        let violationsResults: (ViolationsPage | GuardrailViolation[])[] = [];
        let complianceResults: (ComplianceStats | null)[] = [];
        const previewLimit = 5; // Show first 5 violations on main page

        if (validSelectedEnvironment === 'all') {
          // Load first page of violations from each environment for preview
          const violationsPromises = environmentsData.map((environment: GuardrailEnvironment) =>
            getGuardrailsViolations(environment.id, 'open', 1, previewLimit)
              .catch(() => ({ data: [], meta: null }))
          );
          const compliancePromises = environmentsData.map((environment: GuardrailEnvironment) =>
            getComplianceStats(environment.id).catch(() => null)
          );

          violationsResults = await Promise.all(violationsPromises);
          complianceResults = await Promise.all(compliancePromises);
        } else {
          // Load first page of violations for selected environment only
          const selectedEnv = environmentsData.find((e: GuardrailEnvironment) => e.id === validSelectedEnvironment);
          if (selectedEnv) {
            const [violationsResponse, complianceData] = await Promise.all([
              getGuardrailsViolations(selectedEnv.id, 'open', 1, previewLimit)
                .catch(() => ({ data: [], meta: null })),
              getComplianceStats(selectedEnv.id).catch(() => null),
            ]);
            violationsResults = [violationsResponse];
            complianceResults = [complianceData];
          }
        }

        // Extract violations from response (handle both old array format and new paginated format)
        const allViolations = violationsResults.flatMap(result => {
          if (Array.isArray(result)) {
            return result;
          }
          return result.data || [];
        });
        // Sort violations by severity (critical > high > medium > low) to show most critical first
        setViolations(sortViolationsBySeverity(allViolations));

        // Calculate total violations count from pagination meta or array length
        let totalViolations = 0;
        violationsResults.forEach(result => {
          if (Array.isArray(result)) {
            totalViolations += result.length;
          } else if (result.meta?.total == null) {
            // Fallback to data length if meta not available
            totalViolations += (result.data || []).length;
          } else {
            // Use total from pagination meta for accurate count
            totalViolations += result.meta.total;
          }
        });

        const activeRules = policiesList?.length || 0;

        // Calculate compliance score if any environments have data
        const validComplianceScores = complianceResults.filter(
          (s): s is NonNullable<typeof s> => s != null && s.total_resources > 0
        );
        let complianceScore = null;

        if (validComplianceScores.length > 0) {
          if (validSelectedEnvironment === 'all') {
            // Average across all environments with data
            complianceScore =
              validComplianceScores.reduce((sum, s) => sum + (s.compliance_score || 0), 0) /
              validComplianceScores.length;
          } else {
            // Use selected environment's score
            complianceScore = validComplianceScores[0]?.compliance_score ?? null;
          }
        }

        setOverallStats({
          activeRules,
          openViolations: totalViolations,
          complianceScore: complianceScore === null ? null : Math.round(complianceScore),
        });
      } else {
        setOverallStats({
          activeRules: policiesList?.length || 0,
          openViolations: 0,
          complianceScore: null,
        });
      }
    } catch (err: unknown) {
      console.error('Error loading guardrails data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load guardrails data');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, selectedProjectId, isLoadingClients, selectedEnvironment, user?.role, user?.client_id, filterType, filterId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleGroup = async (group: GuardrailGroup) => {
    setTogglingGroupId(group.id);
    try {
      const newIsActive = !group.is_active;
      await toggleGuardrailGroup(group.id, newIsActive);
      // Optimistically update local state
      setGroups(prev => prev.map(g =>
        g.id === group.id ? { ...g, is_active: newIsActive } : g
      ));
    } catch (err: unknown) {
      console.error('Error toggling group:', err);
    } finally {
      setTogglingGroupId(null);
    }
  };

  const handleScanNow = async () => {
    setScanning(true);
    try {
      const envsToScan = selectedEnvironment === 'all'
        ? environments
        : environments.filter(e => e.id === selectedEnvironment);

      await Promise.all(envsToScan.map(env => triggerEnvironmentScan(env.id)));

      // Reload data after a short delay to let the scan start
      setTimeout(() => loadData(), 3000);
    } catch (err: unknown) {
      console.error('Error triggering scan:', err);
    } finally {
      setScanning(false);
    }
  };

  // Compute policy count per group from loaded policies
  const groupPolicyCounts = policies.reduce<Record<string, number>>((acc, policy) => {
    (policy.group_ids || []).forEach((gid: string) => {
      acc[gid] = (acc[gid] || 0) + 1;
    });
    return acc;
  }, {});

  const filteredPolicies = selectedCategory === 'all'
    ? policies
    : policies.filter(p => p.category === selectedCategory);

  const categories = [
    { value: 'all', label: `All Categories (${policies.length})` },
    { value: 'security', label: `Security (${policies.filter(p => p.category === 'security').length})` },
    { value: 'cost', label: `Cost Management (${policies.filter(p => p.category === 'cost').length})` },
    { value: 'governance', label: `Governance (${policies.filter(p => p.category === 'governance').length})` },
    { value: 'reliability', label: `Reliability (${policies.filter(p => p.category === 'reliability').length})` },
  ];

  const groupMap = groups.reduce<Record<string, string>>((acc, group) => {
    acc[group.id] = group.name;
    return acc;
  }, {});

  const getComplianceDisplayColor = (score: number | null | undefined): string => {
    if (score == null) {
      return 'text-gray-500 dark:text-gray-400';
    }
    if (score >= 95) {
      return 'text-green-600 dark:text-green-400';
    }
    if (score >= 80) {
      return 'text-yellow-600 dark:text-yellow-400';
    }
    return 'text-red-600 dark:text-red-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Guardrails
              </h1>
              <p className="mt-1 text-gray-600 dark:text-gray-400">
                Policy management and compliance monitoring
              </p>
            </div>
          </div>
          <div className="flex items-end gap-3">
            {environments.length > 0 && (
              <Button
                onClick={handleScanNow}
                variant="secondary"
                disabled={scanning}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`} />
                {scanning ? 'Scanning...' : 'Scan Now'}
              </Button>
            )}
            <Button
              onClick={() => navigate('/cloudops/guardrails/environments')}
              variant="primary"
              className="flex items-center gap-2"
            >
              View Environments
            </Button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
          <Button
            onClick={loadData}
            variant="ghost"
            size="sm"
            className="mt-2"
          >
            Try again
          </Button>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Active Rules
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {overallStats.activeRules}
                </p>
              </div>
              <Shield className="w-12 h-12 text-blue-600 dark:text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Open Violations
                </p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">
                  {overallStats.openViolations}
                </p>
              </div>
              <AlertTriangle className="w-12 h-12 text-yellow-600 dark:text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Compliance Score
                </p>
                <p
                  className={`text-3xl font-bold mt-2 ${getComplianceDisplayColor(overallStats.complianceScore)}`}
                >
                  {overallStats.complianceScore === null ? 'No data yet' : `${overallStats.complianceScore}%`}
                </p>
              </div>
              <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Policy Groups Section */}
      {groups.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Policy Groups
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map((group) => {
              const policyCount = groupPolicyCounts[group.id] || group.policy_count || 0;
              const isToggling = togglingGroupId === group.id;
              return (
                <Card key={group.id} className={group.is_active ? '' : 'opacity-60'}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0 mr-3">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
                            {group.name}
                          </h3>
                          {!group.is_active && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                              Disabled
                            </span>
                          )}
                        </div>
                        {group.description && (
                          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                            {group.description}
                          </p>
                        )}
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                          {policyCount} {policyCount === 1 ? 'policy' : 'policies'}
                        </p>
                      </div>
                      <button
                        onClick={() => handleToggleGroup(group)}
                        disabled={isToggling}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                          group.is_active
                            ? 'bg-blue-600'
                            : 'bg-gray-200 dark:bg-gray-600'
                        } ${isToggling ? 'opacity-50 cursor-wait' : ''}`}
                        role="switch"
                        aria-checked={group.is_active}
                        aria-label={`Toggle ${group.name}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            group.is_active ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Category Filters */}
      <div className="mb-6 flex flex-wrap gap-2">
        {categories.map((category) => (
          <button
            key={category.value}
            onClick={() => setSelectedCategory(category.value)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedCategory === category.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {category.label}
          </button>
        ))}
      </div>

      {/* Active Violations Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Active Violations
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/cloudops/guardrails/violations')}
          >
            View All
          </Button>
        </div>
        {violations.length > 0 ? (
          <div className="space-y-4">
            {violations.map((violation) => (
              <ViolationCard
                key={violation.id}
                violation={violation}
                onResolve={() => loadData()}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                No active violations found
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Active Guardrails Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Active Guardrails
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/cloudops/guardrails/policies')}
          >
            View All
          </Button>
        </div>
        {filteredPolicies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredPolicies.map((policy) => {
              const groupNames = (policy.group_ids || [])
                .map((id) => groupMap[id])
                .filter(Boolean);
              return (
                <GuardrailCard
                  key={policy.id}
                  policy={policy}
                  groupNames={groupNames}
                  onEdit={() => navigate(`/cloudops/guardrails/policies/${policy.id}/edit`)}
                  onDelete={() => loadData()}
                />
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                No guardrails found
              </p>
            </CardContent>
          </Card>
        )}
      </div>

    </div>
  );
};

export default GuardrailsPage;
